import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { collection, addDoc, serverTimestamp, getDocs, doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useTicket, useTicketMessages } from '@/hooks/useTickets';
import { Layout } from '@/components/Layout';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { StatusBadge, TypeBadge, PriorityBadge } from '@/components/Badges';
import { timestampToDate, Attachment } from '@/types';

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const MAX_TOTAL_SIZE = 20 * 1024 * 1024;

export function TicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const { ticket, loading: ticketLoading, error: ticketError } = useTicket(id);
  const { messages: allMessages, loading: messagesLoading } = useTicketMessages(id);
  
  // Filter out internal admin notes — customers should never see them
  const messages = allMessages.filter((m) => !m.isInternal);
  
  const [replyContent, setReplyContent] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [ticketAttachments, setTicketAttachments] = useState<Attachment[]>([]);

  // Check if current user owns this ticket
  const isOwner = ticket && currentUser && ticket.ownerId === currentUser.uid;

  // Fetch ticket attachments
  useEffect(() => {
    const fetchAttachments = async () => {
      if (!id) return;
      try {
        const attachmentsSnap = await getDocs(collection(db, 'tickets', id, 'attachments'));
        const allAttachments: Attachment[] = [];
        attachmentsSnap.forEach((doc) => {
          const data = doc.data();
          if (data.attachments && Array.isArray(data.attachments)) {
            allAttachments.push(...data.attachments);
          }
        });
        setTicketAttachments(allAttachments);
      } catch (err) {
        console.error('Error fetching attachments:', err);
      }
    };
    fetchAttachments();
  }, [id]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      const oversized = newFiles.find(f => f.size > MAX_FILE_SIZE);
      if (oversized) {
        alert(`File "${oversized.name}" exceeds 5MB limit`);
        return;
      }
      const totalSize = [...files, ...newFiles].reduce((sum, f) => sum + f.size, 0);
      if (totalSize > MAX_TOTAL_SIZE) {
        alert('Total attachment size exceeds 20MB limit');
        return;
      }
      setFiles(prev => [...prev, ...newFiles]);
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleCloseTicket = async () => {
    if (!id || !ticket) return;
    try {
      await updateDoc(doc(db, 'tickets', id), {
        status: 'CLOSED',
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Error closing ticket:', err);
      alert('Failed to close ticket');
    }
  };

  const uploadFiles = async (ticketId: string, messageId: string): Promise<Attachment[]> => {
    const attachments: Attachment[] = [];
    
    for (const file of files) {
      const storageRef = ref(storage, `tickets/${ticketId}/messages/${messageId}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      
      attachments.push({
        name: file.name,
        url,
        size: file.size,
        type: file.type,
        uploadedAt: new Date() as any,
      });
    }
    
    return attachments;
  };

  const handleReplySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyContent.trim() || !id || !currentUser || !userProfile) return;

    setSubmitting(true);
    try {
      // Upload files first if any
      let attachments: Attachment[] = [];
      if (files.length > 0) {
        // Create a temporary message ID for upload path
        const tempId = Date.now().toString();
        attachments = await uploadFiles(id, tempId);
      }

      const messageData: any = {
        authorId: currentUser.uid,
        authorEmail: currentUser.email!,
        authorName: userProfile.displayName || currentUser.email!.split('@')[0],
        authorRole: userProfile.role,
        content: replyContent.trim(),
        isInternal: false,
        createdAt: serverTimestamp(),
      };

      if (attachments.length > 0) {
        messageData.attachments = attachments;
      }

      await addDoc(collection(db, 'tickets', id, 'messages'), messageData);

      // Bump ticket updatedAt
      await updateDoc(doc(db, 'tickets', id), {
        updatedAt: serverTimestamp(),
      });
      
      setReplyContent('');
      setFiles([]);
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Failed to send message');
    } finally {
      setSubmitting(false);
    }
  };

  if (ticketLoading) {
    return <ProtectedRoute><Layout><LoadingSpinner /></Layout></ProtectedRoute>;
  }

  if (ticketError || !ticket) {
    return (
      <ProtectedRoute><Layout>
        <div className="terminal-alert error">
          {ticketError ? 'Error loading ticket' : 'Ticket not found'}
        </div>
        <button onClick={() => navigate('/tickets')} className="action-btn">Back to My Tickets</button>
      </Layout></ProtectedRoute>
    );
  }

  if (!isOwner) {
    return (
      <ProtectedRoute><Layout>
        <div className="terminal-alert error">Access denied — you do not have permission to view this ticket</div>
        <button onClick={() => navigate('/tickets')} className="action-btn">Back to My Tickets</button>
      </Layout></ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute>
      <Layout>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          {/* Back link */}
          <button
            onClick={() => navigate('/tickets')}
            className="topbar-link"
            style={{ background: 'none', border: 'none', cursor: 'pointer', marginBottom: '16px', padding: 0 }}
          >
            ← Back to My Tickets
          </button>

          {/* Ticket details */}
          <div className="terminal-card">
            <div className="card-header">
              <span className="card-num" style={{ fontFamily: 'Consolas, monospace', fontSize: '9px' }}>
                #{ticket.id.slice(-8).toUpperCase()}
              </span>
              <span className="card-title" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{ticket.title}</span>
              <div className="card-header-right">
                <StatusBadge status={ticket.status} />
                <TypeBadge type={ticket.type} />
                <PriorityBadge priority={ticket.priority} />
                {ticket.status !== 'CLOSED' && (
                  <button onClick={handleCloseTicket} className="action-btn secondary" style={{ minWidth: 'auto', padding: '4px 10px', fontSize: '9px' }}>
                    Close
                  </button>
                )}
              </div>
            </div>
            <div className="card-body">
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <span className="status-chip">CREATED <strong>{timestampToDate(ticket.createdAt) ? format(timestampToDate(ticket.createdAt)!, 'dd MMM yyyy HH:mm') : 'JUST NOW'}</strong></span>
                <span className="status-chip">CHANNEL <strong>{ticket.channel}</strong></span>
              </div>
              <hr className="term-divider" />
              <div>
                <div className="field-label" style={{ marginBottom: '8px' }}>DESCRIPTION</div>
                <div style={{
                  background: 'var(--surface-strong)',
                  border: '1px solid var(--border)',
                  padding: '12px 14px',
                  fontSize: '11px',
                  color: 'var(--text)',
                  whiteSpace: 'pre-wrap',
                  lineHeight: '1.6',
                  letterSpacing: '0.04em',
                  textTransform: 'none',
                }}>
                  {ticket.description}
                </div>
              </div>
              {ticketAttachments.length > 0 && (
                <div>
                  <div className="field-label" style={{ marginBottom: '8px' }}>ATTACHMENTS</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {ticketAttachments.map((attachment, idx) => (
                      <a key={idx} href={attachment.url} target="_blank" rel="noopener noreferrer" className="file-chip" style={{ textDecoration: 'none' }}>
                        {attachment.name} ({(attachment.size / 1024).toFixed(1)} KB)
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Messages */}
          <div className="terminal-card">
            <div className="card-header">
              <span className="card-num">02</span>
              <span className="card-title">MESSAGES</span>
              <div className="card-header-right">
                <span className="stat-chip">COUNT <strong>{messages.length}</strong></span>
              </div>
            </div>
            <div className="card-body">
              {messagesLoading ? (
                <LoadingSpinner />
              ) : messages.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px', color: 'var(--muted)', fontSize: '10px' }}>
                  No messages yet
                </div>
              ) : (
                <div>
                  {messages.map((message) => {
                    const messageDate = timestampToDate(message.createdAt);
                    const isOwnMessage = message.authorId === currentUser?.uid;
                    return (
                      <div key={message.id} className="message-item">
                        <div className="message-meta">
                          <span className="message-author" style={{ color: isOwnMessage ? 'var(--gold)' : 'var(--success)' }}>
                            {message.authorName || message.authorEmail}
                          </span>
                          {message.authorRole === 'ADMIN' && (
                            <span className="term-badge medium" style={{ fontSize: '8px' }}>ADMIN</span>
                          )}
                          <span className="message-time">
                            {messageDate ? format(messageDate, 'dd/MM/yy HH:mm') : 'NOW'}
                          </span>
                        </div>
                        <div className="message-body" style={{ textTransform: 'none' }}>{message.content}</div>
                        {message.attachments && message.attachments.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                            {message.attachments.map((attachment, idx) => (
                              <a key={idx} href={attachment.url} target="_blank" rel="noopener noreferrer" className="file-chip" style={{ textDecoration: 'none' }}>
                                {attachment.name}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Reply */}
          {ticket.status !== 'CLOSED' && (
            <div className="terminal-card">
              <div className="card-header">
                <span className="card-num">03</span>
                <span className="card-title">SEND REPLY</span>
              </div>
              <div className="card-body">
                <form onSubmit={handleReplySubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <textarea
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    className="terminal-textarea"
                    placeholder="Type your message…"
                    required
                    style={{ textTransform: 'none', minHeight: '100px' }}
                  />
                  <div>
                    <input
                      type="file"
                      id="reply-attachments"
                      multiple
                      onChange={handleFileChange}
                      className="hidden"
                      accept="image/*,.pdf,.txt,.log,.json,.zip"
                    />
                    <label htmlFor="reply-attachments" className="action-btn secondary" style={{ cursor: 'pointer', display: 'inline-flex', width: 'fit-content' }}>
                      Attach Files
                    </label>
                    {files.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                        {files.map((file, index) => (
                          <span key={index} className="file-chip">
                            {file.name} ({(file.size / 1024).toFixed(1)} KB)
                            <button type="button" onClick={() => removeFile(index)}>×</button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <button type="submit" disabled={submitting || !replyContent.trim()} className="action-btn large">
                      {submitting ? 'Sending…' : 'Send Reply'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </Layout>
    </ProtectedRoute>
  );
}


